import { ChamferOutline } from "@/components/chamfer";
import { PanelLeftIcon } from "lucide-react";

import { DashboardScheduleCard } from "./DashboardScheduleCard";
import { DashboardScheduleEditAvailabilityButton } from "./DashboardScheduleEditAvailabilityButton";
import { DashboardSchedulePanelViewToggle } from "./DashboardSchedulePanelViewToggle";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";

import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import {
  DashboardInterview,
  DashboardScheduleDaySection,
  DashboardSchedulePanelView,
} from "@/types";

export const ScheduleCalendarMini = ({
  interviews,
  panelView,
  onPanelViewChange,
  onEditAvailability,
  onSelectCandidacy,
  onCollapseToRail,
  showOwnerAvatar = false,
}: {
  interviews: DashboardInterview[];
  panelView?: DashboardSchedulePanelView;
  onPanelViewChange?: (view: DashboardSchedulePanelView) => void;
  onEditAvailability: () => void;
  onSelectCandidacy: (interview: DashboardInterview) => void;
  onCollapseToRail?: () => void;
  showOwnerAvatar?: boolean;
}) => {
  const sections = dashboardScheduleWeek.buildDaySections(interviews);

  const render = () => (
    <Column className="lever-data-table flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lever-md border border-lever-border-light bg-lever-surface-default font-lever-suisse">
      <ChamferOutline />
      <Row className="h-12 shrink-0 items-center justify-between gap-1 border-b border-lever-border-light bg-lever-surface-neutral px-3">
        <Text className="lever-heading-h7">7 day schedule</Text>
        <Row className="items-center gap-1">
          <DashboardScheduleEditAvailabilityButton
            onClick={onEditAvailability}
          />
          {onCollapseToRail ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0 text-lever-text-label"
              onClick={onCollapseToRail}
            >
              <PanelLeftIcon className="h-4 w-4" />
            </Button>
          ) : null}
        </Row>
      </Row>
      <Column className="min-h-0 flex-1 gap-5 overflow-y-auto px-3 pb-4 pt-3">
        {sections.map((section) => (
          <ScheduleCalendarMiniSection
            key={section.key}
            section={section}
            showOwnerAvatar={showOwnerAvatar}
            onSelectCandidacy={onSelectCandidacy}
          />
        ))}
      </Column>
      {panelView && onPanelViewChange ? (
        <Row className="shrink-0 items-center justify-end px-3 py-2.5">
          <DashboardSchedulePanelViewToggle
            value={panelView}
            onChange={onPanelViewChange}
          />
        </Row>
      ) : null}
    </Column>
  );

  return render();
};

const ScheduleCalendarMiniSection = ({
  section,
  showOwnerAvatar = false,
  onSelectCandidacy,
}: {
  section: DashboardScheduleDaySection;
  showOwnerAvatar?: boolean;
  onSelectCandidacy: (interview: DashboardInterview) => void;
}) => {
  const render = () => (
    <Column className="scroll-mt-4 gap-2.5">
      <Row className="items-center gap-2">
        <Text className="shrink-0 font-lever-suisse text-xs font-semibold text-lever-text-label">
          {section.listLabel}
        </Text>
        <div className="h-px flex-1 border-t border-dashed border-lever-border-light" />
      </Row>
      {section.interviews.length === 0 ? (
        <Text className="font-lever-suisse text-xs italic text-lever-text-label">
          No events
        </Text>
      ) : (
        <Column className="gap-2">
          {section.interviews.map((interview) => (
            <DashboardScheduleCard
              key={interview.id}
              interview={interview}
              showOwnerAvatar={showOwnerAvatar}
              onSelect={
                interview.candidacy
                  ? () => {
                      onSelectCandidacy(interview);
                    }
                  : undefined
              }
            />
          ))}
        </Column>
      )}
    </Column>
  );

  return render();
};
